import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthBootstrap } from "../../context/AuthBootstrapContext";
import { useNotifications } from "../../context/NotificationsContext";

const API_BASE = "https://api-2026.fikacreatives.co";

// ── Helpers ─────────────────────────────────────────────────

function formatCycleDate(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}

function getFollowingWeekDates(cycleDateStr) {
  if (!cycleDateStr) return null;
  const d = new Date(cycleDateStr + "T12:00:00");
  const dayOfWeek = d.getDay(); // 0=Sun … 6=Sat
  const daysToNextMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
  const mon = new Date(d);
  mon.setDate(d.getDate() + daysToNextMonday);
  const fmt = (offset) => {
    const x = new Date(mon);
    x.setDate(mon.getDate() + offset);
    return x.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  return { wed: fmt(2), fri: fmt(4), sun: fmt(6) };
}

const SLOT_OPTIONS = [
  { value: "1", emoji: "1️⃣", day: "Wed", time: "6 PM", wkKey: "wed" },
  { value: "2", emoji: "2️⃣", day: "Fri", time: "4 PM", wkKey: "fri" },
  { value: "3", emoji: "3️⃣", day: "Sun", time: "1 PM", wkKey: "sun" },
  { value: "4", emoji: "4️⃣", day: "Can't make it", time: null, wkKey: null },
];

function AvailabilitySlots({ cycleId, cycleDate, initialSlots }) {
  const [selected, setSelected] = useState(() => new Set(initialSlots ?? []));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(!!(initialSlots && initialSlots.length > 0));
  const [error, setError] = useState("");

  const wk = getFollowingWeekDates(cycleDate);

  function toggle(value) {
    if (saved) { setSaved(false); }
    setSelected(prev => {
      const next = new Set(prev);
      if (value === "4") {
        return next.has("4") ? new Set() : new Set(["4"]);
      }
      next.delete("4");
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  }

  async function submit() {
    if (!cycleId || saving) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/fikas/respond`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cycleId, slots: [...selected] }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || "Failed to save");
      setSaved(true);
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1a2e", marginBottom: 4 }}>
        The usual times are:
      </div>
      <div style={{ fontSize: 12, color: "#9B9087", lineHeight: 1.5, marginBottom: 10 }}>
        Select all that apply. On the week of the cycle, you'll receive an invite to a Whatsapp Group with who you're connecting with and where, based on your locations.
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {SLOT_OPTIONS.map(({ value, emoji, day, time, wkKey }) => {
          const isActive = selected.has(value);
          const date = wk?.[wkKey] ?? null;
          return (
            <button
              key={value}
              onClick={() => toggle(value)}
              disabled={saving}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
                padding: "10px 6px",
                border: isActive ? "2px solid #1a1a2e" : "1.5px solid #e0d9d0",
                borderRadius: 12,
                background: isActive ? "#1a1a2e" : "#faf9f7",
                color: isActive ? "#fff" : "#1a1a2e",
                cursor: saving ? "default" : "pointer",
                fontFamily: "inherit",
                transition: "all 0.15s ease",
              }}
            >
              <span style={{ fontSize: 16 }}>{emoji}</span>
              {wkKey ? (
                <>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>Next {day}{date ? `, ${date}` : ""}</span>
                  <span style={{ fontSize: 11, opacity: 0.8 }}>{time}</span>
                </>
              ) : (
                <span style={{ fontSize: 12, fontWeight: 700 }}>{day}</span>
              )}
            </button>
          );
        })}
      </div>
      {saved ? (
        <div style={{ marginTop: 10, fontSize: 12, color: "#5a9a5a", fontWeight: 600 }}>
          ✓ Availability saved
        </div>
      ) : (
        <button
          onClick={submit}
          disabled={saving || selected.size === 0}
          style={{
            marginTop: 10,
            width: "100%",
            padding: "9px 0",
            borderRadius: 10,
            border: "none",
            background: selected.size > 0 ? "#1a1a2e" : "#e0d9d0",
            color: selected.size > 0 ? "#fff" : "#aaa",
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 600,
            cursor: selected.size > 0 && !saving ? "pointer" : "default",
            transition: "background 0.15s ease",
          }}
        >
          {saving ? "Saving…" : "Confirm availability"}
        </button>
      )}
      {error && <div style={{ marginTop: 6, fontSize: 12, color: "#c0392b" }}>{error}</div>}
    </div>
  );
}

function formatDateTime(isoStr) {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  return d.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}


// ── Sub-components ───────────────────────────────────────────

function CupIcon({ size = 36 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "#EDE8DF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="none" stroke="#7B7067" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 8h1a4 4 0 0 1 0 8h-1" />
        <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z" />
        <line x1="6" y1="2" x2="6" y2="4" />
        <line x1="10" y1="2" x2="10" y2="4" />
        <line x1="14" y1="2" x2="14" y2="4" />
      </svg>
    </div>
  );
}

function Avatar({ src, name, size = 44 }) {
  const initials = name
    ? name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  return src ? (
    <img
      src={src}
      alt={name}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        objectFit: "cover",
        border: "2px solid #fff",
      }}
    />
  ) : (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "#C4BAB0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.35,
        color: "#fff",
        fontWeight: 600,
        border: "2px solid #fff",
      }}
    >
      {initials}
    </div>
  );
}

function formatPhone(e164) {
  // +12125551234 → +1 (212) 555-1234
  const digits = e164.replace(/\D/g, "");
  if (digits.length === 11 && digits[0] === "1") {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return e164;
}

function MemberCard({ member }) {
  const specialties = member.creativeSpecialties?.[0] ?? "";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <Avatar
        src={member.profilePicUrl}
        name={`${member.firstName} ${member.lastName}`}
      />
      <div>
        <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e" }}>
          {member.firstName} {member.lastName}
        </div>
        {specialties && (
          <div style={{ fontSize: 13, color: "#7B7067" }}>{specialties}</div>
        )}
      </div>
    </div>
  );
}


// ── Countdown timer ──────────────────────────────────────────

function useCountdown(expiresAt) {
  const [remaining, setRemaining] = useState(() => Math.max(0, new Date(expiresAt) - Date.now()));

  useEffect(() => {
    const end = new Date(expiresAt).getTime();
    if (Date.now() >= end) return;
    const id = setInterval(() => {
      const ms = Math.max(0, end - Date.now());
      setRemaining(ms);
      if (ms <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (remaining <= 0) return "Expired";
  const totalSecs = Math.floor(remaining / 1000);
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ── Coffee Invite Card ────────────────────────────────────────

function formatSuggestedDateTime(date, time) {
  if (!date) return null;
  const d = new Date(date + "T12:00:00");
  const dateStr = d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  return time ? `${dateStr} — ${time}` : dateStr;
}

function CoffeeInviteCard({ invite }) {
  const [accepted, setAccepted] = useState(false);
  const navigate = useNavigate();
  const isSender = invite.role === "sender";
  const name = `${invite.otherPersonFirstName} ${invite.otherPersonLastName}`.trim();
  const firstName = invite.otherPersonFirstName;
  const title = isSender
    ? `You invited ${name} to coffee ☕`
    : `${name} invited you to coffee ☕`;
  const isAcceptedByInvitee = Boolean(invite.acceptedAt) || accepted;
  const countdown = useCountdown(invite.expiresAt);
  const suggestedDateTime = formatSuggestedDateTime(invite.suggestedDate, invite.suggestedTime);
  const suggestedLabel = [suggestedDateTime, invite.suggestedCafe ? `at ${invite.suggestedCafe}` : null].filter(Boolean).join(" ");

  return (
    <div style={styles.card}>
      <div style={styles.cardRow}>
        <CupIcon size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <div
              onClick={() => invite.otherPersonId && navigate(`/profile/${invite.otherPersonId}`)}
              style={{ cursor: invite.otherPersonId ? "pointer" : "default", flexShrink: 0 }}
            >
              <Avatar src={invite.otherPersonProfileImageUrl} name={name} size={36} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={styles.cardTitle}>{title}</div>
              <div style={{ fontSize: 12, color: isAcceptedByInvitee ? "#2a7a4b" : countdown === "Expired" ? "#b42318" : "#7B7067", fontVariantNumeric: "tabular-nums", marginTop: 2 }}>
                {isAcceptedByInvitee ? "Accepted" : countdown === "Expired" ? "Expired" : `Expires in ${countdown}`}
              </div>
            </div>
          </div>

          {invite.message && (
            <div style={{ ...styles.cardSub, fontStyle: "italic", marginTop: 4 }}>
              "{invite.message}"
            </div>
          )}

          {suggestedLabel && (
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", marginTop: 8 }}>
              ☕ Suggested: {suggestedLabel}
            </div>
          )}

          {!isSender && !accepted && (
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                style={{ ...coordStyles.coordBtn, background: "#1a1a2e", fontSize: 13, padding: "8px 20px" }}
                onClick={() => {
                  setAccepted(true);
                  fetch(`${API_BASE}/api/coffee-invites`, {
                    method: "PATCH",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ inviteId: invite.id }),
                  }).catch(() => {});
                }}
              >
                Accept
              </button>
              {invite.senderSharedPhone && invite.otherPersonPhone && (
                <div style={{ fontSize: 12, color: "#7B7067", marginTop: 8, lineHeight: 1.5 }}>
                  If you need to change the location or rearrange a time, text {firstName} at{" "}
                  <a href={`tel:${invite.otherPersonPhone}`} style={{ color: "#7B7067", fontWeight: 600 }}>
                    {formatPhone(invite.otherPersonPhone)}
                  </a>.
                </div>
              )}
            </div>
          )}

          {!isSender && accepted && (
            <div style={{ marginTop: 10, fontSize: 13, color: "#1a1a2e", lineHeight: 1.6, background: "#EDE8DF", borderRadius: 8, padding: "10px 12px" }}>
              By accepting, you'll find a specific time to coordinate with {firstName}. Let {firstName} know via text to confirm you're meeting at the above date and time  
              {invite.senderSharedPhone && invite.otherPersonPhone ? (
                <> at <a href={`tel:${invite.otherPersonPhone}`} style={{ color: "#1a1a2e", fontWeight: 600 }}>{formatPhone(invite.otherPersonPhone)}</a>.</>
              ) : "."}
            </div>
          )}

          {isSender && invite.senderSharedPhone && (
            <div style={{ fontSize: 13, color: "#7B7067", marginTop: 8, fontStyle: "italic" }}>
              Your phone number was shared — {firstName} can reach out to coordinate.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



// ── Match Group Card ──────────────────────────────────────────

function MatchGroupCard({ fika, nextCycleDate, nextCycleId, mySlots }) {
  const isScheduled = fika.fikaStatus === "scheduled";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cycleIsPast = new Date(fika.cycleDate + "T12:00:00") < today;
  const displayCycleDate = cycleIsPast ? nextCycleDate : fika.cycleDate;

  return (
    <div style={{
      background: "#fff",
      borderRadius: 16,
      padding: "20px 20px 22px",
      marginBottom: 16,
      boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
      border: "1px solid #EDE8DF",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <CupIcon size={36} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1a2e", fontFamily: "Suisse-Medium" }}>
            {isScheduled ? "You've been matched!" : "Group Fikas"}
          </div>
          {isScheduled && (
            <div style={{ fontSize: 13, color: "#7B7067", marginTop: 2 }}>
              {[fika.proposedPlace, formatDateTime(fika.proposedTime)].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
      </div>

      {!isScheduled ? (
        <div style={{
          fontSize: 13,
          color: "#7B7067",
          lineHeight: 1.6,
          background: "#F5F2ED",
          borderRadius: 10,
          padding: "10px 14px",
        }}>
          {displayCycleDate
            ? <>You'll get a text on {formatCycleDate(displayCycleDate)} for 3 days/times if you're available for that following week. If you're available, reply all dates/times you are free.</>
            : <>You'll get a text soon with 3 days/times. If you're available, reply all dates/times you are free.</>
          }{" "}We try our best to matchmake you with creatives you're looking to connect with. If you can't make it let us know 24 hours in advance.
          <AvailabilitySlots cycleId={nextCycleId} cycleDate={displayCycleDate} initialSlots={mySlots} />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {fika.members.map((m) => (
            <MemberCard key={m.personId} member={m} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Past Fika Card ────────────────────────────────────────────

function PastFikaCard({ fika }) {
  const missed = fika.groupStatus === "inactive" && fika.cycleStatus === "closed";
  return (
    <div style={{ ...styles.card, opacity: missed ? 0.6 : 1 }}>
      <div style={styles.cardRow}>
        <CupIcon size={40} />
        <div style={styles.pastCard}>
          <div style={styles.pastTitle}>
            {missed
              ? formatCycleDate(fika.cycleDate)
              : (fika.proposedPlace ? `${fika.proposedPlace} – ` : "") +
                (fika.proposedTime ? formatDateTime(fika.proposedTime) : formatCycleDate(fika.cycleDate))}
          </div>
          {missed ? (
            <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>
              {fika.subtext || "Sorry we weren't able to make this fika happen"}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
              {fika.members.map((m) => (
                <MemberCard key={m.personId} member={m} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Fika Events Section ───────────────────────────────────────

function FikaEventsSection({ events, isNYC }) {
  if (!events.length && !isNYC) return null;

  const monthLabel = new Date().toLocaleString("en-US", { month: "long" });

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <CupIcon size={48} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#1a1a2e", fontFamily: "Suisse-Medium" }}>
            {monthLabel} Fikas
          </div>
          <div style={{ fontSize: 14, color: "#7B7067", marginTop: 2, lineHeight: 1.4 }}>
            {isNYC
              ? "Stay tuned for upcoming Fika events in NYC to casually meet other creatives."
              : "RSVP to any of the following events to meet other creatives over coffee in different settings, coworking or happy hour!"}
          </div>
        </div>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 16,
      }}>
        {events.map((event) => {
          const venue = event.event_location || event.location || event.event_name || "Untitled";
          const date = event.date_label || "";
          const time = event.time_label || "";
          const area = event.area || "";
          const spotsLabel = event.spots_label || (event.max_rsvps ? `Up to ${event.max_rsvps} RSVPs` : "Up to 10 RSVPs");

          return (
            <div key={event.event_id || event.id} style={{
              background: "#fff",
              borderRadius: 16,
              padding: "22px 20px",
              boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: "#1a1a2e", lineHeight: 1.25 }}>
                    {venue}
                  </div>
                  {area && (
                    <div style={{ fontSize: 13, color: "#7B7067", marginTop: 4 }}>{area}</div>
                  )}
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  {date && <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e" }}>{date}</div>}
                  {time && <div style={{ fontSize: 13, color: "#7B7067", marginTop: 2 }}>{time}</div>}
                </div>
              </div>
              <a
                href={event.event_url || "#"}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "block",
                  marginTop: 4,
                  background: "#17154f",
                  color: "#fff",
                  textAlign: "center",
                  borderRadius: 8,
                  padding: "12px 0",
                  fontWeight: 700,
                  fontSize: 15,
                  textDecoration: "none",
                  letterSpacing: "0.04em",
                }}
              >
                I'm in to Fika
              </a>
              <div style={{ fontSize: 12, color: "#7B7067", marginTop: 4 }}>{spotsLabel}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────

export default function FikaPage() {
  const { person } = useAuthBootstrap();
  const isNYC = (person?.city || "").toLowerCase().includes("new york");
  const { fikaData: data, fikaLoading: loading } = useNotifications();
  const personId = person?.id;
  const [tab, setTab] = useState("upcoming");
  const [coffeeInvites, setCoffeeInvites] = useState([]);
  const [fikaEvents, setFikaEvents] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE}/api/coffee-invites`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : { invites: [] })
      .then((d) => setCoffeeInvites(d.invites ?? []))
      .catch(() => {});
  }, [personId]);

  useEffect(() => {
    const url = personId
      ? `${API_BASE}/api/events?person_id=${personId}`
      : `${API_BASE}/api/events`;
    fetch(url)
      .then((r) => r.ok ? r.json() : { events: [] })
      .then((d) => {
        setFikaEvents(d.events ?? []);
      })
      .catch(() => {});
  }, [personId]);


  const showUpcoming = tab === "upcoming";
  const allUpcomingWithMembers = (data?.upcoming ?? []).filter((f) => f.members?.length > 0);
  const pendingGroups = (data?.upcoming ?? []).filter((f) => !f.members?.length);
  const currentCycleDate = allUpcomingWithMembers.length > 0
    ? allUpcomingWithMembers.reduce((max, f) => (f.cycleDate > max ? f.cycleDate : max), allUpcomingWithMembers[0].cycleDate)
    : null;
  const matchGroups = currentCycleDate
    ? allUpcomingWithMembers.filter((f) => f.cycleDate === currentCycleDate)
    : allUpcomingWithMembers;
  const hasMatches = matchGroups.length > 0;
  const activeCycleDate = pendingGroups[0]?.cycleDate ?? data?.nextCycleDate ?? null;
  const trialExpired = (() => {
    if (!person?.created_at) return false;
    const end = new Date(person.created_at);
    end.setDate(end.getDate() + 14);
    return Date.now() > end.getTime();
  })();
  const showUpgradeBanner =
    !loading && data?.subscriptionStatus === "free" && trialExpired;

  const pastList = [
    ...(data?.past ?? []),
  ].sort((a, b) => b.cycleDate.localeCompare(a.cycleDate));

  const now = Date.now();
  const activeCoffeeInvites = coffeeInvites.filter(inv => new Date(inv.expiresAt).getTime() > now);
  const pastCoffeeInvites   = coffeeInvites.filter(inv => inv.acceptedAt && new Date(inv.expiresAt).getTime() <= now)
    .sort((a, b) => new Date(b.acceptedAt).getTime() - new Date(a.acceptedAt).getTime());

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <div style={styles.tabs}>
          <button
            onClick={() => setTab("upcoming")}
            style={{ ...styles.tab, ...(showUpcoming ? styles.tabActive : {}) }}
          >
            Upcoming Fikas
          </button>
          {(pastList.length > 0 || pastCoffeeInvites.length > 0) && (
            <button
              onClick={() => setTab("past")}
              style={{ ...styles.tab, ...(!showUpcoming ? styles.tabActive : {}) }}
            >
              Past Fikas
            </button>
          )}
        </div>

        {showUpcoming && (
          <p style={{ margin: "0 0 20px", fontSize: 13, color: "#888", lineHeight: 1.6 }}>
            <strong style={{ color: "#555" }}>What's a fika?</strong> In Sweden, people commonly take a break and connect over coffee and a sweet treat. Inspired by the ritual, Fika connects creatives over coffee in 3 ways for friendships, collaborations, and opportunities. This creative community is built on shared accountability, divergent thinking and repeated interactions. 
          </p>
        )}


        {showUpcoming ? (
          <>
            {matchGroups.map((fika) => (
              <MatchGroupCard key={fika.groupId} fika={fika} nextCycleDate={data?.nextCycleDate} nextCycleId={data?.nextCycleId} mySlots={data?.mySlots} />
            ))}
            {activeCoffeeInvites.length > 0 && (
              <div style={styles.list}>
                {activeCoffeeInvites.map((invite) => (
                  <CoffeeInviteCard key={invite.id} invite={invite} />
                ))}
              </div>
            )}

            {isNYC && (
              <div style={styles.list}>
                <div style={styles.card}>
                  <div style={styles.cardRow}>
                    <CupIcon size={40} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.cardTitle}>1:1 Fikas</div>
                      <div style={{ fontSize: 13, color: "#7B7067", marginTop: 6, lineHeight: 1.6 }}>
                        Find someone's work inspiring? Send a coffee and invites expire after 24 hours.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!hasMatches && (
              <div style={styles.list}>
                <div style={styles.card}>
                  <div style={styles.cardRow}>
                    <CupIcon size={40} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.cardTitle}>Group Fikas</div>
                      {activeCycleDate && (
                        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#b0956b", marginTop: 4 }}>
                          {formatCycleDate(activeCycleDate)}
                        </div>
                      )}
                      <div style={{ fontSize: 13, color: "#7B7067", marginTop: 6, lineHeight: 1.6 }}>
                        {activeCycleDate
                          ? <>You'll get a text on {formatCycleDate(activeCycleDate)} for 3 days/times if you're available for that following week. If you're available, reply all dates/times you are free, and you'll get a text with who you're meeting, when and where, based on your location. If you can't make it let us know 24 hours in advance.</>
                          : "Make sure to reply to the options on Fika text to find a time that works great for you."}
                      </div>
                      {activeCycleDate && (
                        <AvailabilitySlots cycleId={data?.nextCycleId} cycleDate={activeCycleDate} initialSlots={data?.mySlots} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <FikaEventsSection events={fikaEvents} isNYC={isNYC} />
          </>
        ) : (
          <>
            {loading && <div style={styles.empty}>Loading…</div>}
            {!loading && data && (
              <div style={{ position: "relative" }}>
                <div style={{ filter: showUpgradeBanner ? "blur(4px)" : "none", pointerEvents: showUpgradeBanner ? "none" : "auto", userSelect: showUpgradeBanner ? "none" : "auto" }}>
                  <div style={styles.list}>
                    {pastList.map((fika) => (
                      <PastFikaCard key={fika.groupId || fika.cycleId} fika={fika} />
                    ))}
                    {pastCoffeeInvites.map((invite) => (
                      <CoffeeInviteCard key={invite.id} invite={invite} />
                    ))}
                  </div>
                </div>
                {showUpgradeBanner && (
                  <div style={styles.upgradeBanner}>
                    <div style={styles.upgradeIcon}>☕</div>
                    <div style={styles.upgradeTitle}>Your free trial has ended</div>
                    <div style={styles.upgradeBody}>
                      Become a member to keep meeting new creatives every 2 weeks.
                    </div>
                    <a href="/apply/pay?no_trial=true" style={styles.upgradeBtn}>
                      Upgrade to continue
                    </a>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────
const styles = {
  upgradeBanner: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    background: "#fff",
    borderRadius: 16,
    padding: "32px 28px",
    textAlign: "center",
    boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
    zIndex: 10,
    width: "min(380px, 90%)",
  },
  upgradeIcon: {
    fontSize: 36,
    marginBottom: 12,
  },
  upgradeTitle: {
    fontWeight: 700,
    fontSize: 18,
    color: "#1a1a2e",
    marginBottom: 8,
    fontFamily: "Suisse-Medium",
  },
  upgradeBody: {
    fontSize: 14,
    color: "#7B7067",
    lineHeight: 1.6,
    marginBottom: 20,
  },
  upgradeBtn: {
    display: "inline-block",
    background: "#1a1a2e",
    color: "#fff",
    borderRadius: 8,
    padding: "12px 24px",
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
    fontFamily: "inherit",
  },
  page: {
    minHeight: "100vh",
    background: "#F5F2ED",
    fontFamily: "Suisse",
    color: "#1a1a2e",
  },
  main: {
    maxWidth: 760,
    margin: "0 auto",
    padding: "40px 24px",
  },
  tabs: {
    display: "flex",
    gap: 24,
    marginBottom: 36,
  },
  tab: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: "Suisse-Medium",
    fontSize: 18,
    fontWeight: 500,
    color: "#aaa",
    padding: 0,
    transition: "color 0.15s",
  },
  tabActive: {
    color: "#1a1a2e",
    fontWeight: 500,
    fontFamily: "Suisse-Medium"
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },
  card: {
    padding: "8px 0 24px",
    borderBottom: "none",
  },
  cardRow: {
    display: "flex",
    gap: 16,
    alignItems: "flex-start",
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: 15,
    fontFamily: "Suisse-Medium",
    color: "#1a1a2e",
    marginBottom: 4,
  },
  cardSub: {
    fontSize: 14,
    color: "#444",
    lineHeight: 1.5,
  },
  availabilityPrompt: {
    fontSize: 15,
    color: "#1a1a2e",
    lineHeight: 1.6,
    marginTop: 16,
    marginBottom: 4,
  },
  matchChip: {
    background: "#EDE8DF",
    borderRadius: 8,
    padding: "12px 16px",
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  matchLabel: {
    fontWeight: 700,
    fontSize: 13,
    color: "#1a1a2e",
  },
  inputRow: {
    display: "flex",
    gap: 10,
    marginTop: 10,
    flexWrap: "wrap",
  },
  input: {
    flex: 1,
    minWidth: 160,
    border: "1px solid #D5CFC6",
    borderRadius: 6,
    padding: "10px 12px",
    fontSize: 14,
    fontFamily: "inherit",
    background: "#fff",
    color: "#1a1a2e",
    outline: "none",
  },
  btn: {
    background: "#1a1a2e",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "10px 20px",
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  pastCard: {
    flex: 1,
    minWidth: 0,
  },
  pastTitle: {
    fontWeight: 700,
    fontSize: 15,
    color: "#1a1a2e",
  },
  empty: {
    color: "#999",
    fontSize: 15,
    paddingTop: 12,
  },
};

const coordStyles = {
  coordBtn: {
    background: "#17154f",
    color: "#fff",
    border: "none",
    borderRadius: 20,
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  phoneLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "#EDE8DF",
    color: "#1a1a2e",
    border: "none",
    borderRadius: 20,
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 600,
    textDecoration: "none",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
};

